import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { DMChatMessage, DMChatRoom } from "../../types/dm";
import { ChatConnectionStatus, MessageStatus, MessageType } from "../../types/chat";
import { queryGet } from "../../utils/queryClient";
import useChatRoomMessages from "../../components/api/queries/useCharRoomMessages";

interface UseChatRoomReturn {
  messages: DMChatMessage[];
  chatRoom: DMChatRoom | null;
  connectionStatus: ChatConnectionStatus;
  messageStatuses: Map<string, MessageStatus>;
  sendMessage: (content: string, messageType?: MessageType, imageUrls?: string[]) => Promise<void>;
  refetchMessages: () => void;
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

const DEFAULT_TIMEOUT = 10000;

export function useChatRoom(chatRoomId: string): UseChatRoomReturn {
  const [messageStatuses, setMessageStatuses] = useState<Map<string, MessageStatus>>(new Map());
  const [connectionStatus, setConnectionStatus] = useState<ChatConnectionStatus>({
    isConnected: true,
    isReconnecting: false,
    lastConnectedAt: new Date()
  });

  // 폴링 관리
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPollingActive, setIsPollingActive] = useState(true);
  const mountedRef = useRef(true);

  // 채팅방 정보 조회
  const { data: chatRoom } = useQuery(
    ['chatRoom', chatRoomId],
    () => queryGet(`/chat_rooms/${chatRoomId}`),
    {
      enabled: !!chatRoomId,
      select: (r: any) => r.chatRoom as DMChatRoom,
      cacheTime: 0
    }
  );

  // 메시지 목록 조회
  const {
    data,
    isFetching,
    fetchNextPage,
    hasNextPage,
    refetch
  } = useChatRoomMessages(chatRoomId);

  // 메시지 데이터 처리
  const messages = data?.pages
    ?.flatMap((page: any) => page.chatRoomMessages.map((message: DMChatMessage) => message))
    .reverse() || [];

  // 컴포넌트 마운트/언마운트 관리
  useEffect(() => {
    mountedRef.current = true;
    setIsPollingActive(true);

    return () => {
      mountedRef.current = false;
      setIsPollingActive(false);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  // 폴링 로직
  useEffect(() => {
    if (!isPollingActive || !mountedRef.current || !chatRoomId) return;

    const startPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      pollingIntervalRef.current = setInterval(() => {
        if (isPollingActive && mountedRef.current && !document.hidden) {
          refetch();
        }
      }, DEFAULT_TIMEOUT);
    };

    // 페이지 가시성 변경 처리
    const handleVisibilityChange = () => {
      if (document.hidden || !mountedRef.current) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else if (isPollingActive) {
        refetch();
        startPolling();
      }
    };

    // 포커스 변경 처리
    const handleFocus = () => {
      if (isPollingActive && mountedRef.current) {
        refetch();
        startPolling();
      }
    };

    const handleBlur = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    // 초기 폴링 시작
    startPolling();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [isPollingActive, chatRoomId, refetch]);

  const sendMessage = useCallback(async (
    content: string,
    messageType: MessageType = 'text',
    imageUrls?: string[]
  ) => {
    const tempId = `temp_${Date.now()}`;

    // 낙관적 업데이트
    setMessageStatuses(prev => new Map(prev.set(tempId, { id: tempId, status: 'sending' })));

    try {
      // 실제 전송 로직은 DMMessageInputBox에서 처리
      // 여기서는 상태만 관리
      setMessageStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(tempId, { id: tempId, status: 'sent' });
        return newMap;
      });

      // 메시지 전송 후 즉시 새로고침
      setTimeout(() => {
        if (mountedRef.current) {
          refetch();
        }
      }, 100);

    } catch (error) {
      setMessageStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(tempId, {
          id: tempId,
          status: 'failed',
          error: error instanceof Error ? error.message : 'Unknown error'
        });
        return newMap;
      });
    }
  }, [refetch]);

  const refetchMessages = useCallback(() => {
    if (mountedRef.current) {
      refetch();
    }
  }, [refetch]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetching, hasNextPage, fetchNextPage]);

  return {
    messages,
    chatRoom: chatRoom || null,
    connectionStatus,
    messageStatuses,
    sendMessage,
    refetchMessages,
    isLoading: isFetching,
    hasMore: !!hasNextPage,
    loadMore
  };
}
